import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { RECAPTCHA_V2_SITE_KEY } from '../config/env';

/**
 * Görünür reCAPTCHA **v2** ("Ben robot değilim") widget'ı. Google'ın `api.js?render=explicit`
 * betiğini ilk kullanımda enjekte eder ve `grecaptcha.render` ile bir div'e çizer. Site key
 * yapılandırılmamışsa (`REACT_APP_RECAPTCHA_V2_SITE_KEY` boş) hiçbir şey render etmez —
 * çağıran taraf bu durumda görünmez v3 akışına düşer.
 *
 * Çözülen token `onChange(token)` ile yukarı verilir; süresi dolunca ya da reset'lenince
 * `onChange('')` çağrılır. `resetKey` değiştiğinde widget sıfırlanır (başarısız giriş sonrası).
 * `window.grecaptcha` tipi `./recaptcha.ts`'te tek yerde tanımlı (v2 + v3 paylaşır).
 */

let scriptPromise: Promise<void> | null = null;

function loadExplicitScript(): Promise<void> {
  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    window.__tfwOnRecaptchaV2Load = () => resolve();
    const script = document.createElement('script');
    script.src =
      'https://www.google.com/recaptcha/api.js?onload=__tfwOnRecaptchaV2Load&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('reCAPTCHA yüklenemedi'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface RecaptchaCheckboxProps {
  onChange: (token: string) => void;
  /** Değeri değişince widget reset'lenir (ör. başarısız login denemesi sayacı). */
  resetKey?: number;
}

/** Site key yoksa `true` — çağıran taraf v3'e düşer. */
export const isRecaptchaV2Enabled = (): boolean => !!RECAPTCHA_V2_SITE_KEY;

export function RecaptchaCheckbox({ onChange, resetKey = 0 }: RecaptchaCheckboxProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!RECAPTCHA_V2_SITE_KEY) return;
    let cancelled = false;
    loadExplicitScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        const grecaptcha = window.grecaptcha;
        if (!grecaptcha) return;
        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: RECAPTCHA_V2_SITE_KEY,
          callback: (token) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(''),
          'error-callback': () => onChangeRef.current(''),
        });
      })
      .catch(() => {
        /* betik yüklenemedi — çağıran taraf zorunlu tutuyorsa buton kilitli kalır */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Dışarıdan reset isteği.
  useEffect(() => {
    if (resetKey === 0) return;
    if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(widgetIdRef.current);
      onChangeRef.current('');
    }
  }, [resetKey]);

  if (!RECAPTCHA_V2_SITE_KEY) return null;

  return <Box ref={containerRef} sx={{ minHeight: 78 }} aria-label="reCAPTCHA doğrulaması" />;
}
